const REGISTRATION_COMPLETED_EVENT = "registration_completed";
const LEAD_CREATED_EVENT = "lead_created";
const CUSTOMER_ACTION_PROPS = Object.freeze({
  type: "customer_action",
});
const LEGACY_REGISTRATION_COMPLETED_STORAGE_PREFIX =
  "openai_ads_registration_completed";
const PRODUCTION_HOSTS = Object.freeze({
  "zoni.edu": true,
  "www.zoni.edu": true,
  "highschool.zoni.edu": true,
});

function getBrowserWindow() {
  return typeof window === "undefined" ? null : window;
}

function getSessionStorage() {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return null;

  try {
    return browserWindow.sessionStorage;
  } catch (error) {
    return null;
  }
}

function getMemoryStore() {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return null;

  try {
    browserWindow.__zoniOpenAIAdsTrackedEvents =
      browserWindow.__zoniOpenAIAdsTrackedEvents || Object.create(null);
    return browserWindow.__zoniOpenAIAdsTrackedEvents;
  } catch (error) {
    return null;
  }
}

function isDebugEnabled() {
  const browserWindow = getBrowserWindow();
  if (!browserWindow || !browserWindow.location) return false;

  try {
    if (PRODUCTION_HOSTS[browserWindow.location.hostname]) {
      return false;
    }
    return (
      new URLSearchParams(browserWindow.location.search).get(
        "openai_ads_debug"
      ) === "1"
    );
  } catch (error) {
    return false;
  }
}

function debugLog(message, error) {
  if (!isDebugEnabled()) return;

  const browserWindow = getBrowserWindow();
  const consoleRef = browserWindow && browserWindow.console;
  if (!consoleRef || typeof consoleRef.debug !== "function") return;

  try {
    consoleRef.debug(message, error);
  } catch (debugError) {
    // Debug logging must never affect user flows.
  }
}

function normalizeEventId(eventIdOrKey) {
  try {
    return String(eventIdOrKey || "").trim();
  } catch (error) {
    return "";
  }
}

function getSafeEventId(eventIdOrKey) {
  const eventId = normalizeEventId(eventIdOrKey);
  if (!eventId || eventId.includes("@")) {
    return "";
  }

  return eventId.slice(0, 128);
}

function getStorageKey(eventName, eventId) {
  return `openai_ads:${eventName}:${eventId || "default"}`;
}

function getLegacyStorageKey(eventName, eventId) {
  if (eventName !== REGISTRATION_COMPLETED_EVENT) return "";
  return `${LEGACY_REGISTRATION_COMPLETED_STORAGE_PREFIX}:${
    eventId || "default"
  }`;
}

function hasTracked(storageKeys) {
  const storage = getSessionStorage();
  if (storage) {
    try {
      for (const storageKey of storageKeys) {
        if (storageKey && storage.getItem(storageKey)) {
          return true;
        }
      }
    } catch (error) {
      // Fall back to the in-memory guard below.
    }
  }

  const memoryStore = getMemoryStore();
  if (!memoryStore) return false;

  try {
    return storageKeys.some(
      (storageKey) => storageKey && memoryStore[storageKey]
    );
  } catch (error) {
    return false;
  }
}

function markTracked(storageKeys) {
  const storage = getSessionStorage();
  if (storage) {
    try {
      storageKeys.forEach((storageKey) => {
        if (storageKey) {
          storage.setItem(storageKey, "1");
        }
      });
    } catch (error) {
      // The in-memory guard still prevents duplicate firing on this page.
    }
  }

  const memoryStore = getMemoryStore();
  if (!memoryStore) return;

  try {
    storageKeys.forEach((storageKey) => {
      if (storageKey) {
        memoryStore[storageKey] = true;
      }
    });
  } catch (error) {
    // Duplicate persistence is best-effort only.
  }
}

function buildEventOptions(eventId) {
  if (!eventId) {
    return {};
  }

  return {
    event_id: eventId,
  };
}

function trackOpenAIAdsEvent(eventName, eventIdOrKey = "") {
  try {
    const browserWindow = getBrowserWindow();
    if (
      !browserWindow ||
      typeof browserWindow.zoniOpenAIAdsTrack !== "function"
    ) {
      return false;
    }

    const normalizedEventName = String(eventName || "").trim();
    if (!normalizedEventName) {
      return false;
    }

    const eventId = getSafeEventId(eventIdOrKey);
    const storageKeys = [
      getStorageKey(normalizedEventName, eventId),
      getLegacyStorageKey(normalizedEventName, eventId),
    ].filter(Boolean);

    if (hasTracked(storageKeys)) {
      return false;
    }

    const tracked = browserWindow.zoniOpenAIAdsTrack(
      normalizedEventName,
      CUSTOMER_ACTION_PROPS,
      buildEventOptions(eventId)
    );

    if (tracked) {
      markTracked(storageKeys);
    }

    return Boolean(tracked);
  } catch (error) {
    debugLog("OpenAI Ads tracking skipped", error);
    return false;
  }
}

export function trackRegistrationCompleted(eventIdOrKey = "") {
  try {
    return trackOpenAIAdsEvent(REGISTRATION_COMPLETED_EVENT, eventIdOrKey);
  } catch (error) {
    debugLog("OpenAI registration_completed tracking skipped", error);
    return false;
  }
}

export function trackLeadCreated(eventIdOrKey = "") {
  try {
    return trackOpenAIAdsEvent(LEAD_CREATED_EVENT, eventIdOrKey);
  } catch (error) {
    debugLog("OpenAI lead_created tracking skipped", error);
    return false;
  }
}
